import { AlertTriangle, Check, ChevronsUpDown, Clock, FileSignature, Trash2 } from "lucide-react";
import { useState } from "react";
import { CurrencyInput } from "@/components/buy-log/CurrencyInput";
import { ScheduledValueCell } from "@/components/buy-log/ScheduledValueCell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TableCell, TableRow } from "@/components/ui/table";
import type { BudgetAwardStatus, BudgetLineItem } from "@/hooks/use-budgets";
import type { Company } from "@/hooks/use-companies";
import { formatCurrency, toNumber } from "@/lib/formatters";

export interface BuyLogRowPatch {
  awardValue?: string;
  allowance?: string;
  holdsAtBuy?: string;
  awardedCompanyId?: string | null;
  isAllowance?: boolean;
  scheduledValueText?: string | null;
  originalBudgetedAmt?: string;
}

const AWARD_STATUS_STYLE: Record<BudgetAwardStatus, { label: string; className: string }> = {
  not_awarded: {
    label: "Not awarded",
    className: "bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200",
  },
  partial: {
    label: "Partial",
    className: "bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200",
  },
  awarded: {
    label: "Awarded",
    className: "bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200",
  },
};

interface BuyLogRowProps {
  lineItem: BudgetLineItem;
  companies: Company[];
  canEdit: boolean;
  onPatch: (patch: BuyLogRowPatch) => void;
  onCreateCommitment: () => void;
  onDelete: () => void;
  onViewHistory?: () => void;
  onViewCommitment?: (commitmentId: string) => void;
  /**
   * When set, the Cost Code cell is suppressed — hierarchy sections show the
   * code in a section header so repeating it per row is noise.
   */
  hideCostCode?: boolean;
  /**
   * Highlights the row briefly to draw the eye after the user returns from
   * the "Create Commitment" page. Cleared by the parent on a timer.
   */
  recentlyCommitted?: boolean;
}

/**
 * A single row on the Buy Log. Used by both the Grid and Hierarchy views.
 *
 * Editing model: each numeric field is a controlled `<Input>` backed by local
 * state that syncs back to the server on blur (only when the value actually
 * changed). The subcontractor picker saves immediately on change.
 */
export function BuyLogRow({
  lineItem,
  companies,
  canEdit,
  onPatch,
  onCreateCommitment,
  onDelete,
  onViewHistory,
  onViewCommitment,
  hideCostCode,
  recentlyCommitted,
}: BuyLogRowProps) {
  // With CurrencyInput, local draft state lives inside the input itself.
  // We compute totals off the server values, which are source-of-truth.
  const hasTextScheduled = !!lineItem.scheduledValueText;
  const scheduledNumeric = hasTextScheduled ? 0 : toNumber(lineItem.originalBudgetedAmt);
  const totalAward =
    toNumber(lineItem.awardValue) + toNumber(lineItem.allowance) + toNumber(lineItem.holdsAtBuy);
  const variance = scheduledNumeric - totalAward;

  // Variance color now keys off award state (AC#5): gray when the line has
  // no commitments yet, green when under budget + awarded, red when over.
  const varianceClass = hasTextScheduled
    ? "text-gray-500"
    : lineItem.awardStatus === "not_awarded"
      ? "text-gray-500"
      : variance >= 0
        ? "text-emerald-700"
        : "text-red-600";

  const hasCommitment = Boolean(lineItem.commitment) || (lineItem.commitmentCount ?? 0) > 0;
  const canCreateCommitment =
    canEdit && !hasCommitment && Boolean(lineItem.awardedCompanyId) && totalAward > 0;

  function handleSubcontractorChange(companyId: string) {
    const next = companyId === "__none__" ? null : companyId;
    if (next === lineItem.awardedCompanyId) return;
    onPatch({ awardedCompanyId: next });
  }

  const awardStatusStyle = AWARD_STATUS_STYLE[lineItem.awardStatus];

  const [subOpen, setSubOpen] = useState(false);
  const [subSearch, setSubSearch] = useState("");

  function handleSubOpenChange(open: boolean) {
    setSubOpen(open);
    if (!open) setSubSearch("");
  }

  const subQ = subSearch.toLowerCase();
  const filteredCompanies = subQ
    ? companies.filter((c) => c.companyName.toLowerCase().includes(subQ))
    : companies;

  return (
    <TableRow
      className={
        recentlyCommitted
          ? "bg-amber-50 ring-2 ring-inset ring-amber-300 transition-colors duration-700 hover:bg-amber-100"
          : "hover:bg-gray-50"
      }
    >
      {hideCostCode ? null : (
        <TableCell className="text-sm font-medium text-gray-900 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            {lineItem.costCode?.code ?? "--"}
            {lineItem.isAllowance ? (
              <span
                className="inline-flex items-center rounded bg-indigo-50 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide text-indigo-700"
                title="Allowance line — flows through to G703 as an allowance"
              >
                ALW
              </span>
            ) : null}
          </div>
        </TableCell>
      )}
      <TableCell className="text-sm text-gray-700 min-w-[12rem]">
        <div className="flex items-center gap-1.5">
          {!lineItem.originalBudgetedAmt || Number(lineItem.originalBudgetedAmt) === 0 ? (
            <span title="No budget amount allocated" className="inline-flex">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
            </span>
          ) : null}
          {lineItem.description}
        </div>
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <ScheduledValueCell
          numericValue={lineItem.originalBudgetedAmt}
          textValue={lineItem.scheduledValueText ?? null}
          disabled={!canEdit}
          onCommit={(next) =>
            onPatch({
              originalBudgetedAmt: next.numeric,
              scheduledValueText: next.text,
            })
          }
        />
      </TableCell>
      <TableCell className="text-right">
        <CurrencyInput
          value={lineItem.awardValue}
          disabled={!canEdit}
          ariaLabel="Award value"
          onCommit={(next) => onPatch({ awardValue: next })}
          className="w-28"
        />
      </TableCell>
      <TableCell className="text-right">
        <CurrencyInput
          value={lineItem.allowance}
          disabled={!canEdit}
          ariaLabel="Allowance"
          onCommit={(next) => onPatch({ allowance: next })}
          className="w-24"
        />
      </TableCell>
      <TableCell className="text-right">
        <CurrencyInput
          value={lineItem.holdsAtBuy}
          disabled={!canEdit}
          ariaLabel="Holds at buy"
          onCommit={(next) => onPatch({ holdsAtBuy: next })}
          className="w-24"
        />
      </TableCell>
      <TableCell className="text-sm text-gray-900 text-right whitespace-nowrap">
        {formatCurrency(totalAward)}
      </TableCell>
      <TableCell className={`text-sm text-right whitespace-nowrap ${varianceClass}`}>
        {hasTextScheduled ? "—" : formatCurrency(variance)}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${awardStatusStyle.className}`}
        >
          {awardStatusStyle.label}
        </span>
      </TableCell>
      <TableCell className="text-center">
        <Checkbox
          aria-label="Mark as allowance line"
          checked={lineItem.isAllowance}
          disabled={!canEdit}
          onCheckedChange={(next) => onPatch({ isAllowance: next === true })}
        />
      </TableCell>
      <TableCell className="min-w-[14rem]">
        {canEdit ? (
          <Popover open={subOpen} onOpenChange={handleSubOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={subOpen}
                className="h-8 w-56 justify-between font-normal text-sm"
              >
                <span className="truncate">
                  {lineItem.awardedCompanyId
                    ? (companies.find((c) => c.id === lineItem.awardedCompanyId)?.companyName ??
                      "Unassigned")
                    : "Unassigned"}
                </span>
                <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search company…"
                  value={subSearch}
                  onValueChange={setSubSearch}
                />
                <CommandList>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        handleSubcontractorChange("__none__");
                        handleSubOpenChange(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-3.5 w-3.5 ${!lineItem.awardedCompanyId ? "opacity-100" : "opacity-0"}`}
                      />
                      Unassigned
                    </CommandItem>
                    {filteredCompanies.map((c) => (
                      <CommandItem
                        key={c.id}
                        onSelect={() => {
                          handleSubcontractorChange(c.id);
                          handleSubOpenChange(false);
                        }}
                      >
                        <Check
                          className={`mr-2 h-3.5 w-3.5 ${lineItem.awardedCompanyId === c.id ? "opacity-100" : "opacity-0"}`}
                        />
                        {c.companyName}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : (
          <div className="text-sm text-gray-900">
            {lineItem.awardedCompany?.companyName ?? "--"}
          </div>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {hasCommitment && lineItem.commitment ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onViewCommitment?.(lineItem.commitment!.id)}
              className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 hover:underline"
            >
              {lineItem.commitment.commitmentNumber}
            </button>
            {(lineItem.commitmentCount ?? 0) > 1 ? (
              <span
                className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700"
                title={`${lineItem.commitmentCount} linked commitments`}
              >
                +{(lineItem.commitmentCount ?? 1) - 1}
              </span>
            ) : null}
          </div>
        ) : canCreateCommitment ? (
          <Button variant="outline" size="sm" onClick={onCreateCommitment} className="h-8">
            <FileSignature className="mr-1.5 h-3.5 w-3.5" />
            Create Commitment
          </Button>
        ) : null}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {onViewHistory ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onViewHistory}
            className="h-8 ml-1 text-gray-500 hover:text-gray-900"
            title="View edit history"
          >
            <Clock className="h-3.5 w-3.5" />
          </Button>
        ) : null}
        {canEdit && !hasCommitment ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-8 ml-1 text-gray-400 hover:text-red-600"
            title="Delete line item"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
